'use client';

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button } from '@/components/ui/Button';
import { cn } from '@/lib/utils';
import { toast } from '@/lib/notifications';
import type { ClarifyType, ClarificationSubmissionMeta } from '@/lib/chat/clarify';
import { ArrowPathIcon, ClipboardDocumentListIcon } from '@heroicons/react/24/outline';
import { trackClarifyEvent } from '@/lib/clarifyAnalytics';

type Props = {
  type: ClarifyType;
  questions: string[];
  requestId: string;
  originRunId?: string;
  conversationId?: string;
  language?: 'en' | 'ar';
  stepLabel?: string | null;
  agentLabel?: string;
  className?: string;
  onSubmit: (payload: ClarificationSubmissionMeta) => Promise<void>;
  onSkip: (payload: ClarificationSubmissionMeta) => Promise<void>;
  onDismiss?: () => void;
};

const CHAR_LIMIT = 600;

const QUALITY_REVIEW_TEXT = {
  en: {
    heading: 'I noticed a potential issue with this draft',
    helper: 'Review the suggestion below and choose how to proceed.',
    submit: 'Apply suggestion',
    skip: 'Keep as is',
  },
  ar: {
    heading: 'لاحظت مشكلة محتملة في هذه المسودة',
    helper: 'راجع الاقتراح أدناه واختر كيفية المتابعة.',
    submit: 'تطبيق الاقتراح',
    skip: 'الإبقاء كما هو',
  },
} as const;

const TEXT = {
  en: {
    heading: 'Before I draft, could you confirm:',
    helper: 'Answer what you can — placeholders are fine for the rest.',
    optional: 'Optional',
    placeholder: 'Type your answer or leave blank for a placeholder',
    counter: (used: number, max: number) => `${used}/${max} characters`,
    overLimit: (over: number) => `${over} over limit`,
    submit: 'Use these answers',
    submitting: 'Submitting…',
    skip: 'Continue with placeholders',
    skipping: 'Continuing…',
    error: 'Something went wrong sending these answers.',
    retry: 'Retry',
    copy: 'Copy answers',
    copied: 'Answers copied',
    dismiss: 'Dismiss form',
    confirmDismiss: 'Discard your answers and hide the form?',
    examplePrefix: 'e.g.',
  },
  ar: {
    heading: 'قبل أن أكتب، هل يمكنك توضيح التالي؟',
    helper: 'أجب قدر المستطاع ويمكن ترك البقية كعناصر نائبة.',
    optional: 'اختياري',
    placeholder: 'اكتب إجابتك أو اتركها فارغة لاستخدام عنصر نائب',
    counter: (used: number, max: number) => `${used}/${max} حرفًا`,
    overLimit: (over: number) => `تجاوز الحد بـ ${over}`,
    submit: 'اعتمد هذه الإجابات',
    submitting: 'جارٍ الإرسال…',
    skip: 'تابع مع العناصر النائبة',
    skipping: 'جارٍ المتابعة…',
    error: 'حدث خطأ أثناء إرسال هذه الإجابات.',
    retry: 'أعد المحاولة',
    copy: 'انسخ الإجابات',
    copied: 'تم نسخ الإجابات',
    dismiss: 'إخفاء النموذج',
    confirmDismiss: 'هل تريد تجاهل إجاباتك وإخفاء النموذج؟',
    examplePrefix: 'مثال',
  }
} as const;

const EXAMPLE_HINTS = [
  { pattern: /audience|customer|reader/i, en: 'VPs of comms at fintech unicorns', ar: 'مدراء الاتصالات في شركات التكنولوجيا المالية' },
  { pattern: /embargo|date|timing/i, en: 'Embargo May 8 at 8am ET', ar: 'حظر نشر حتى ٨ مايو الساعة ٨ صباحًا بتوقيت نيويورك' },
  { pattern: /stat|metric|proof|result/i, en: '120% YoY growth, Series B in Q4', ar: 'نمو ١٢٠٪ على أساس سنوي، جولة تمويل ب في الربع الرابع' },
  { pattern: /contact|media|press/i, en: 'Jane Smith — jane@acme.com · (555) 123-4567', ar: 'أحمد خالد — ahmed@example.com · 966501234567+' },
  { pattern: /cta|call to action|action/i, en: 'Schedule a briefing for the exclusives list', ar: 'تنسيق مقابلة حصرية مع فريقنا التنفيذي' },
] as const;

export default function ClarifyForm({
  type,
  questions,
  requestId,
  originRunId,
  conversationId,
  language = 'en',
  stepLabel,
  agentLabel,
  className,
  onSubmit,
  onSkip,
  onDismiss,
}: Props) {
  const locale = language === 'ar' ? 'ar' : 'en';
  const isQualityReview = type === 'quality_review';
  const qrText = QUALITY_REVIEW_TEXT[locale];
  const strings = {
    ...TEXT[locale],
    ...(isQualityReview
      ? {
          heading: qrText.heading,
          helper: qrText.helper,
          submit: qrText.submit,
          skip: qrText.skip,
        }
      : {}),
  };
  const formRef = useRef<HTMLDivElement>(null);
  const firstFieldRef = useRef<HTMLTextAreaElement | null>(null);
  const storageKey = conversationId ? `clarify-form:${conversationId}:${requestId}` : null;
  const [answers, setAnswers] = useState<string[]>(() => questions.map(() => ''));
  const [draftHydrated, setDraftHydrated] = useState(false);
  const [activeAction, setActiveAction] = useState<'submit' | 'skip' | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const hasDirtyAnswers = useMemo(() => answers.some((ans) => ans.trim().length > 0), [answers]);
  const hasOverLimit = useMemo(() => answers.some((ans) => ans.length > CHAR_LIMIT), [answers]);
  const latestAnswersRef = useRef(answers);
  const submittedRef = useRef(false);
  const skippedRef = useRef(false);
  const formShownAtRef = useRef(Date.now());

  useEffect(() => {
    submittedRef.current = false;
    skippedRef.current = false;
  }, [requestId]);

  const writeDraft = (nextAnswers: string[]) => {
    if (!storageKey || typeof window === 'undefined') return;
    try {
      if (nextAnswers.every((ans) => !ans.trim())) {
        window.localStorage.removeItem(storageKey);
        return;
      }
      window.localStorage.setItem(storageKey, JSON.stringify({ answers: nextAnswers, updatedAt: Date.now() }));
    } catch {}
  };

  const clearDraft = () => {
    if (!storageKey || typeof window === 'undefined') return;
    try {
      window.localStorage.removeItem(storageKey);
    } catch {}
  };

  useEffect(() => {
    const defaults = questions.map(() => '');
    setDraftHydrated(false);
    if (!storageKey || typeof window === 'undefined') {
      setAnswers(defaults);
      setSubmitError(null);
      setDraftHydrated(true);
      return;
    }
    try {
      const raw = window.localStorage.getItem(storageKey);
      if (!raw) {
        setAnswers(defaults);
      } else {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed?.answers) && parsed.answers.length === questions.length) {
          setAnswers(parsed.answers);
        } else {
          setAnswers(defaults);
        }
      }
    } catch {
      setAnswers(defaults);
    }
    setSubmitError(null);
    setDraftHydrated(true);
  }, [storageKey, questions]);

  useEffect(() => {
    firstFieldRef.current?.focus();
  }, [requestId]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (!draftHydrated) return;
    writeDraft(answers);
  }, [answers, draftHydrated]);

  useEffect(() => {
    latestAnswersRef.current = answers;
  }, [answers]);

  useEffect(() => {
    formShownAtRef.current = Date.now();
    trackClarifyEvent('clarify_form_shown', {
      requestId,
      type,
      questionCount: questions.length,
      agentLabel,
      language: locale,
    });
    return () => {
      if (!submittedRef.current && !skippedRef.current) {
        const answeredCount = latestAnswersRef.current.filter((ans) => ans && ans.trim().length > 0).length;
        trackClarifyEvent('clarify_form_abandoned', {
          requestId,
          type,
          questionCount: questions.length,
          agentLabel,
          language: locale,
          answeredCount,
          timeOpenMs: Date.now() - formShownAtRef.current,
        });
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agentLabel, requestId, questions.length, type, locale]);

  useEffect(() => {
    const node = formRef.current;
    if (!node) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        if (!onDismiss) return;
        if (!hasDirtyAnswers || window.confirm(strings.confirmDismiss)) {
          onDismiss();
        }
        return;
      }
      if (event.key === 'Tab') {
        const focusable = Array.from(
          node.querySelectorAll<HTMLElement>(
            'button, [href], input, textarea, select, [tabindex]:not([tabindex="-1"])'
          )
        ).filter((el) => !el.hasAttribute('disabled'));
        if (focusable.length === 0) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }
    };
    node.addEventListener('keydown', handleKeyDown);
    return () => {
      node.removeEventListener('keydown', handleKeyDown);
    };
  }, [hasDirtyAnswers, onDismiss, strings.confirmDismiss]);

  const getExample = (question: string): string | null => {
    const match = EXAMPLE_HINTS.find((hint) => hint.pattern.test(question));
    if (!match) return null;
    return locale === 'ar' ? match.ar : match.en;
  };

  const compilePayload = (): ClarificationSubmissionMeta => {
    const normalized = answers.map((ans) => ans.trim());
    const answerList = normalized.map((ans) => (ans ? ans : '[placeholder]'));
    const placeholderCount = answerList.filter((ans) => ans === '[placeholder]').length;
    const formattedSections = answerList.map((ans, idx) => `${idx + 1}. ${questions[idx]}\n${ans}`);
    const compiledText = `Clarifications for ${type} draft:\n\n${formattedSections.join('\n\n')}`;
    return {
      requestId,
      type,
      questions,
      answers: answerList,
      placeholderCount,
      compiledText,
      agentLabel,
      originRunId,
    };
  };

  const handleSubmit = async () => {
    if (hasOverLimit || activeAction) return;
    const wasRetry = !!submitError;
    const start = Date.now();
    setActiveAction('submit');
    setSubmitError(null);
    const payload = compilePayload();
    if (wasRetry) {
      trackClarifyEvent('clarify_form_retry', {
        requestId,
        type,
        questionCount: questions.length,
        agentLabel,
      });
    }
    try {
      await onSubmit(payload);
      submittedRef.current = true;
      const answeredCount = payload.answers.filter((ans) => ans && ans !== '[placeholder]').length;
      trackClarifyEvent('clarify_form_submitted', {
        requestId,
        type,
        questionCount: questions.length,
        agentLabel,
        placeholderCount: payload.placeholderCount,
        answeredCount,
        timeToSubmitMs: Date.now() - start,
      });
      clearDraft();
      setAnswers(questions.map(() => ''));
    } catch (error: any) {
      trackClarifyEvent('clarify_form_error', {
        requestId,
        type,
        questionCount: questions.length,
        agentLabel,
        action: 'submit',
        message: error?.message || strings.error,
      });
      setSubmitError(error?.message || strings.error);
    } finally {
      setActiveAction(null);
    }
  };

  const handleSkip = async () => {
    if (activeAction) return;
    const start = Date.now();
    setActiveAction('skip');
    setSubmitError(null);
    const payload = { ...compilePayload(), skipped: true };
    try {
      await onSkip(payload);
      skippedRef.current = true;
      trackClarifyEvent('clarify_form_skipped', {
        requestId,
        type,
        questionCount: questions.length,
        agentLabel,
        placeholderCount: payload.placeholderCount,
        timeToSkipMs: Date.now() - start,
      });
      clearDraft();
      setAnswers(questions.map(() => ''));
    } catch (error: any) {
      trackClarifyEvent('clarify_form_error', {
        requestId,
        type,
        questionCount: questions.length,
        agentLabel,
        action: 'skip',
        message: error?.message || strings.error,
      });
      setSubmitError(error?.message || strings.error);
    } finally {
      setActiveAction(null);
    }
  };

  const handleCopy = async () => {
    const payload = compilePayload();
    try {
      await navigator.clipboard.writeText(payload.compiledText);
      toast.success(strings.copied);
    } catch {
      toast.error(strings.error);
    }
  };

  return (
    <div
      ref={formRef}
      className={cn('space-y-4', className)}
      dir={locale === 'ar' ? 'rtl' : 'ltr'}
      aria-live="polite"
      data-testid="clarify-form"
    >
      <div>
        <h4 className="text-sm font-semibold text-foreground">{strings.heading}</h4>
        <p className="text-xs text-muted-foreground">{strings.helper}</p>
      </div>
      <div className="space-y-4">
        {questions.map((question, idx) => {
          const fieldId = `${requestId}-field-${idx}`;
          const answer = answers[idx] || '';
          const overBy = Math.max(0, answer.length - CHAR_LIMIT);
          const example = getExample(question);
          const isOptional = true;
          return (
            <div key={fieldId} className="space-y-2">
              <div className="flex items-center justify-between gap-2 text-xs font-medium text-muted-foreground">
                <label htmlFor={fieldId} className="truncate">
                  {idx + 1}. {question}
                </label>
                {isOptional && <span className="text-xs uppercase tracking-wide">{strings.optional}</span>}
              </div>
              <textarea
                id={fieldId}
                ref={idx === 0 ? firstFieldRef : undefined}
                value={answer}
                onChange={(event) => {
                  const next = event.target.value;
                  setAnswers((prev) => {
                    const nextAnswers = prev.map((ans, i) => (i === idx ? next : ans));
                    // Persist immediately so fast reloads do not lose draft input.
                    writeDraft(nextAnswers);
                    return nextAnswers;
                  });
                }}
                disabled={!!activeAction}
                className={cn(
                  'w-full min-h-[72px] rounded-xl border bg-background/80 p-3 text-sm shadow-inner focus:outline-none focus:ring-2 focus:ring-primary/30 transition',
                  overBy > 0 ? 'border-red-400/80' : 'border-border'
                )}
                placeholder={strings.placeholder}
                aria-invalid={overBy > 0}
                aria-describedby={`${fieldId}-meta`}
                data-testid={`clarify-field-${idx}`}
              />
              <div
                id={`${fieldId}-meta`}
                className="flex flex-wrap items-center justify-between gap-2 text-xs text-muted-foreground"
              >
                {example && (
                  <span className="italic">
                    {strings.examplePrefix}: {example}
                  </span>
                )}
                <span className={cn('ml-auto', overBy > 0 && 'text-red-400')}>
                  {overBy > 0
                    ? `${strings.counter(answer.length, CHAR_LIMIT)} • ${strings.overLimit(overBy)}`
                    : strings.counter(answer.length, CHAR_LIMIT)}
                </span>
              </div>
            </div>
          );
        })}
      </div>
      {submitError && (
        <div className="rounded-xl border border-red-500/40 bg-red-500/10 p-3 text-xs text-red-100" role="status">
          {submitError}
          <div className="mt-2 flex flex-wrap gap-2">
            <Button
              size="sm"
              variant="secondary"
              className="text-xs"
              onClick={handleSubmit}
              disabled={!!activeAction}
              data-testid="clarify-retry"
            >
              {strings.retry}
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="text-xs"
              onClick={handleCopy}
              data-testid="clarify-copy"
            >
              <ClipboardDocumentListIcon className="h-4 w-4" />
              <span className="ml-1">{strings.copy}</span>
            </Button>
          </div>
        </div>
      )}
      <div className="sticky bottom-0 left-0 right-0 bg-background/90 backdrop-blur pt-3">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              onClick={handleSubmit}
              disabled={!!activeAction || hasOverLimit}
              className="flex-1 sm:flex-none"
              data-testid="clarify-submit"
            >
              {activeAction === 'submit' ? (
                <span className="inline-flex items-center gap-2">
                  <ArrowPathIcon className="h-4 w-4 animate-spin" />
                  {strings.submitting}
                </span>
              ) : (
                strings.submit
              )}
            </Button>
            <Button
              type="button"
              variant="ghost"
              onClick={handleSkip}
              disabled={!!activeAction}
              className="flex-1 sm:flex-none"
              data-testid="clarify-skip"
            >
              {activeAction === 'skip' ? (
                <span className="inline-flex items-center gap-2">
                  <ArrowPathIcon className="h-4 w-4 animate-spin" />
                  {strings.skipping}
                </span>
              ) : (
                strings.skip
              )}
            </Button>
          </div>
          {onDismiss && (
            <button
              type="button"
              className="text-xs text-muted-foreground underline-offset-4 hover:underline"
              onClick={() => {
                if (!hasDirtyAnswers || window.confirm(strings.confirmDismiss)) {
                  onDismiss();
                }
              }}
            >
              {strings.dismiss}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
